/**
 * @packageDocumentation
 * @category Component
 */

export type BubbleSide = 'bottom' | 'left' | 'right' | 'top';

export type BubbleAnchor = { bottom: number; height: number; left: number; right: number; top: number; width: number };

export type BubbleSize = { height: number; width: number };

export type BubbleViewport = { height: number; width: number };

export type BubblePosition = { left: number; side: BubbleSide; top: number };

const MARGIN = 8;
const GAP = 8;

/**
 * Where a bubble sits beside its trigger, and which side it ended up on.
 *
 * Measured, never CSS anchor positioning: `position-try` fallbacks are inert in
 * current Chromium (verified empirically 2026-08-16), so an edge-adjacent
 * trigger kept its bubble at an overflowing base position. The preferred side
 * flips when it has no room, the cross axis clamps to the viewport, and the
 * returned `side` is what the arrow must follow: an arrow left pointing at the
 * preferred side after a flip points away from the trigger.
 */
export const placeBubble = (anchor: BubbleAnchor, bubble: BubbleSize, viewport: BubbleViewport, preferred: BubbleSide): BubblePosition => {
  const fits: Record<BubbleSide, boolean> = {
    bottom: anchor.bottom + GAP + bubble.height <= viewport.height - MARGIN,
    left: anchor.left - GAP - bubble.width >= MARGIN,
    right: anchor.right + GAP + bubble.width <= viewport.width - MARGIN,
    top: anchor.top - GAP - bubble.height >= MARGIN,
  };
  const flipped: Record<BubbleSide, BubbleSide> = { bottom: 'top', left: 'right', right: 'left', top: 'bottom' };
  const side = fits[preferred] || !fits[flipped[preferred]] ? preferred : flipped[preferred];

  if (side === 'top' || side === 'bottom') {
    return {
      left: Math.max(MARGIN, Math.min(anchor.left + anchor.width / 2 - bubble.width / 2, viewport.width - bubble.width - MARGIN)),
      side,
      top: side === 'top' ? anchor.top - GAP - bubble.height : anchor.bottom + GAP,
    };
  }
  return {
    left: side === 'left' ? anchor.left - GAP - bubble.width : anchor.right + GAP,
    side,
    top: Math.max(MARGIN, Math.min(anchor.top + anchor.height / 2 - bubble.height / 2, viewport.height - bubble.height - MARGIN)),
  };
};
